/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { MessageBarType } from "@fluentui/react";
import { trace } from "owa-trace";
import { orchestrator } from "satcheljs";
import {
    MEMBER_NOT_IN_TEAM,
    MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS,
    MESSAGE_BAR_AUTO_DISMISS_DELAY_MS
} from "sh-application/../StaffHubConstants";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { IMemberEntity, ITagEntity, MemberStates, TagEntity } from "sh-models";
import { ECSConfigKey, ECSConfigService, InstrumentationService, MemberDataService, TagDataService } from "sh-services";
import { TeamStore } from "sh-stores";
import { incrementTagPeopleCount } from "sh-stores/sh-tag-store/mutators/incrementTagPeopleCount";
import StringsStore from "sh-strings/store";
import { setCurrentlySelectedTeamMembers, setIsSaving, setShowInlineError, setTeamMemberToAdd } from "..";
import setGlobalMessage from "../../../../actions/setGlobalMessage";
import { appViewState } from "../../../../store";
import addMembers from "../actions/addMembers";
import fireFocusedBasedAccessibilityAlert from "../../../accessibilityAlert/lib/orchestrators/fireFocusedBasedAccessibilityAlert";

export default orchestrator(addMembers, async actionMessage => {
    // The tag passed into this orchestrator does not include any of the new members we want to add.
    // We clone the tag into tagCopy and then add new members to the tagCopy.
    const tag = actionMessage.tag;
    let membersToAdd = actionMessage.members;
    const closeModal = actionMessage.onCloseClick;
    const tagCopy: ITagEntity = TagEntity.clone(tag);
    const { teamId } = TeamStore();
    const _strings = StringsStore().registeredStringModules.get("memberEditor").strings;
    const newMembersFoundInCDL = checkIfAnyMemberIdsWereCreated(membersToAdd);
    if (tagCopy) {
        // filter out members that already exist in the tag
        membersToAdd = membersToAdd.filter(member => tagCopy.memberIds.indexOf(member.id) === -1);
        tagCopy.userIds ??= [];

        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableTeamMemberSync)) {
            // Build an ordered set of userIds: first from tagCopy.memberIds (via TeamStore().members), then from membersToAdd
            const orderedUserIds = new Set<string>();
            tagCopy.memberIds.forEach(memberId => {
                const memberEntity = TeamStore().members.get(memberId);
                if (!memberEntity) {
                    // Member not found in team store, skip
                    return;
                }
                if (memberEntity.state !== MemberStates.Active) {
                    // Member is not active, skip
                    return;
                }
                const userId = memberEntity.userId;
                if (userId) {
                    orderedUserIds.add(userId);
                }
            });
            membersToAdd.forEach(member => {
                if (member.userId) {
                    orderedUserIds.add(member.userId);
                }
            });
            tagCopy.userIds = Array.from(orderedUserIds);

            // Set memberIds array to empty in the new flow. FLW Service is designed to handle either a lst of memberIds or a list of UserIds, but not both in the same request.
            tagCopy.memberIds = [];
        } else {
            membersToAdd.forEach(member => {
                tagCopy.memberIds.push(member.id);
            });
            tagCopy.userIds = [];
        }

        try {
            await TagDataService.saveTag(
                tagCopy,
                false /* isNewTag */,
                false /* optimistic */,
                false /* setIsFocused */,
                false /* updateCacheOnly */,
                true /* throwError */
            );
            setCurrentlySelectedTeamMembers(null);
            if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NewAddMembersViewEnabled)) {
                // Set the member to add to the first member in the list
                // When the flag is diabled, there is only one member in the list
                setTeamMemberToAdd(membersToAdd[0] ?? null);
            }
            if (
                ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableTeamMemberSync) &&
                newMembersFoundInCDL
            ) {
                await MemberDataService.getMembers(teamId, true);
            }
            membersToAdd.forEach(member => {
                incrementTagPeopleCount(tag.id);
            });
            InstrumentationService.logEvent(InstrumentationService.events.MemberAddedToGroupAction, [
                getGenericEventPropertiesObject(InstrumentationService.properties.StatusSucceeded, "true"),
                getGenericEventPropertiesObject(InstrumentationService.properties.NumOfItems, membersToAdd.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.IsNewGroup, tag.memberIds.length === 0 ? "true" : "false"),
                getGenericEventPropertiesObject(InstrumentationService.properties.NewMembersFoundInCDL, newMembersFoundInCDL)
            ]);
            if (closeModal) {
                closeModal();
            }
            fireFocusedBasedAccessibilityAlert({alertText: _strings.get("groupMembersAddSuccess").format(tagCopy.name)});
            setGlobalMessage(
                appViewState().globalMessageViewState,
                _strings.get("groupMembersAddSuccess").format(tagCopy.name),
                MessageBarType.success,
                null,
                null,
                true,
                true,
                MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
            );
        } catch (error) {
            trace.warn("[MemberEditor] Add members failed");
            setShowInlineError(true);

            InstrumentationService.logEvent(InstrumentationService.events.MemberErrorDuringCommitClicked, [
                getGenericEventPropertiesObject(InstrumentationService.properties.Error, error),
                getGenericEventPropertiesObject(InstrumentationService.properties.StatusSucceeded, "false"),
                getGenericEventPropertiesObject(InstrumentationService.properties.NumOfItems, membersToAdd.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.IsNewGroup, tag.memberIds.length === 0 ? "true" : "false"),
                getGenericEventPropertiesObject(InstrumentationService.properties.NewMembersFoundInCDL, newMembersFoundInCDL)
            ]);
            InstrumentationService.trackException(error, "addMembers");
        } finally {
            setIsSaving(false);
        }
    }
});

// If any of the members to add do not have an id, it means they are new members that need to be created.
// This is used to determine if we need to refresh the member list after adding members.
const checkIfAnyMemberIdsWereCreated = (membersToAdd: IMemberEntity[]): boolean => {
    return membersToAdd.some(member => {
        return member.id === MEMBER_NOT_IN_TEAM;
    });
};